'use client';

import {RecaptchaScript} from '@gfiber/third-parties/google';
import {US_STATES} from '@gfiber/typesmith/us_states';
import {zodResolver} from '@hookform/resolvers/zod';
import type React from 'react';
import {useId} from 'react';
import {Controller, type SubmitHandler, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {TermsAndConditions} from '@/app/forms/_components/TermsAndConditions/TermsAndConditions';
import {useSubmit} from '@/app/forms/_hooks/useSubmit';
import {REGISTRY} from '@/app/forms/_registry/client';
import * as constants from '@/app/forms/constants';
import {
  BUILDING_TYPES_MAP,
  PROPERTY_RELATIONSHIPS_MAP,
  YES,
  YES_NO_MAP,
} from '@/app/forms/i18n';
import {ApiError, type InferFormValues} from '@/app/forms/types';
import {
  Checkbox,
  FormError,
  SelectField,
  SelectItem,
  TextArea,
  TextField,
} from '@/components';
import {isRequired, maxLength} from '@/lib/utils/common';
import {
  getRootError,
  getSchemaShape,
  handleError,
  onSubmitError,
  tSafe,
} from '../../utils';
import {Description, FormDescription} from '../Description/Description';
import {SubmitButton} from '../SubmitButton/SubmitButton';
import {SuccessMessage} from '../SuccessMessage/SuccessMessage';
import styles from './FiberInterestForm.module.scss';

function FiberInterestForm(): React.JSX.Element {
  const {t} = useTranslation();

  const config = REGISTRY.FiberInterestForm;
  type FiberInterestFormData = InferFormValues<typeof config>;

  const {
    control,
    formState: {errors, isSubmitted, isValid},
    handleSubmit,
    setError,
  } = useForm<FiberInterestFormData>({
    defaultValues: {
      companyName: '',
      propertyName: '',
      street: '',
      city: '',
      state: '',
      zipCode: '',
      numberOfUnits: '',
      typeOfBuilding: '',
      isPropertyFullyBuilt: '',
      additionalPropertyInfo: '',
      propertyRelationship: '',
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      termsAndConditions: '',
    },
    mode: 'onChange',
    resolver: zodResolver(config.schema),
  });
  const fields = getSchemaShape(config.schema);
  const {mutate, isPending, isSuccess} = useSubmit<FiberInterestFormData>(
    config.id,
  );
  const id = useId();

  const onSubmit: SubmitHandler<FiberInterestFormData> = async data => {
    const recaptchaToken = await window.grecaptcha?.enterprise.execute(
      constants.RECAPTCHA_SITE_KEY,
      {action: config.recaptchaAction || 'submit_form'},
    );

    if (!recaptchaToken) {
      handleError(
        new ApiError(t('errors.recaptcha'), {
          code: 'RECAPTCHA_FAILED',
          message: 'reCAPTCHA token could not be obtained',
        }),
        setError,
        fields,
      );
      return;
    }

    mutate(
      {data, recaptchaToken},
      {
        onError: error => {
          handleError(error, setError, fields);
        },
      },
    );
  };

  return (
    <>
      <h1 className="title">{t(config.title)}</h1>
      {isSuccess ? (
        <SuccessMessage />
      ) : (
        <form
          className="form"
          onSubmit={handleSubmit(onSubmit, onSubmitError)}
          noValidate
        >
          <fieldset disabled={isPending}>
            <FormDescription>
              <Description>{t('forms.fiberInterest.description')}</Description>
            </FormDescription>
            <div className="field">
              <Controller
                control={control}
                name="companyName"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.fiberInterest.companyName')}
                    id={`companyName-${id}`}
                    required={isRequired(fields.companyName)}
                    errorText={tSafe(errors.companyName?.message, t)}
                    maxLength={maxLength(fields.companyName)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="propertyName"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.fiberInterest.propertyName')}
                    id={`propertyName-${id}`}
                    required={isRequired(fields.propertyName)}
                    errorText={tSafe(errors.propertyName?.message, t)}
                    maxLength={maxLength(fields.propertyName)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="street"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.fiberInterest.street')}
                    id={`street-${id}`}
                    required={isRequired(fields.street)}
                    errorText={tSafe(errors.street?.message, t)}
                    maxLength={maxLength(fields.street)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="city"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.fiberInterest.city')}
                    id={`city-${id}`}
                    required={isRequired(fields.city)}
                    errorText={tSafe(errors.city?.message, t)}
                    maxLength={maxLength(fields.city)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                name="state"
                control={control}
                render={({field, fieldState: {error}}) => (
                  <SelectField
                    {...field}
                    placeholder={t('forms.shared.selectOne')}
                    invalid={!!error}
                    label={t('forms.fiberInterest.state')}
                    required={isRequired(fields.state)}
                    errorText={tSafe(errors.state?.message, t)}
                  >
                    {US_STATES.map(state => (
                      <SelectItem
                        key={state.code}
                        value={state.code}
                      >
                        {state.name}
                      </SelectItem>
                    ))}
                  </SelectField>
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="zipCode"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.fiberInterest.zipCode')}
                    id={`zipCode-${id}`}
                    required={isRequired(fields.zipCode)}
                    errorText={tSafe(errors.zipCode?.message, t)}
                    maxLength={maxLength(fields.zipCode)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="numberOfUnits"
                render={({field}) => (
                  <TextField
                    {...field}
                    type="number"
                    label={t('forms.fiberInterest.numberOfUnits')}
                    id={`numberOfUnits-${id}`}
                    required={isRequired(fields.numberOfUnits)}
                    errorText={tSafe(errors.numberOfUnits?.message, t)}
                    maxLength={maxLength(fields.numberOfUnits)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                name="typeOfBuilding"
                control={control}
                render={({field, fieldState: {error}}) => (
                  <SelectField
                    {...field}
                    placeholder={t('forms.shared.selectOne')}
                    invalid={!!error}
                    label={t('forms.fiberInterest.typeOfBuildingLabel')}
                    required={isRequired(fields.typeOfBuilding)}
                    errorText={tSafe(errors.typeOfBuilding?.message, t)}
                  >
                    {BUILDING_TYPES_MAP.map(item => (
                      <SelectItem
                        key={item.id}
                        value={item.value}
                      >
                        {t(item.i18nKey, {defaultValue: item.id})}
                      </SelectItem>
                    ))}
                  </SelectField>
                )}
              />
            </div>
            <div className="field">
              <Controller
                name="isPropertyFullyBuilt"
                control={control}
                render={({field, fieldState: {error}}) => (
                  <SelectField
                    {...field}
                    placeholder={t('forms.shared.selectOne')}
                    invalid={!!error}
                    label={t('forms.fiberInterest.fullyBuiltLabel')}
                    required={isRequired(fields.isPropertyFullyBuilt)}
                    errorText={tSafe(errors.isPropertyFullyBuilt?.message, t)}
                  >
                    {YES_NO_MAP.map(item => (
                      <SelectItem
                        key={item.id}
                        value={item.value}
                      >
                        {t(item.i18nKey, {defaultValue: item.id})}
                      </SelectItem>
                    ))}
                  </SelectField>
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="additionalPropertyInfo"
                render={({field}) => (
                  <TextArea
                    {...field}
                    label={t('forms.fiberInterest.additionalInfoLabel')}
                    placeholder={t(
                      'forms.fiberInterest.additionalInfoPlaceholder',
                    )}
                    id={`additionalPropertyInfo-${id}`}
                    required={isRequired(fields.additionalPropertyInfo)}
                    errorText={tSafe(errors.additionalPropertyInfo?.message, t)}
                    maxLength={maxLength(fields.additionalPropertyInfo)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                name="propertyRelationship"
                control={control}
                render={({field, fieldState: {error}}) => (
                  <SelectField
                    {...field}
                    placeholder={t('forms.shared.selectOne')}
                    invalid={!!error}
                    label={t('forms.fiberInterest.relationshipLabel')}
                    required={isRequired(fields.propertyRelationship)}
                    errorText={tSafe(errors.propertyRelationship?.message, t)}
                  >
                    {PROPERTY_RELATIONSHIPS_MAP.map(item => (
                      <SelectItem
                        key={item.id}
                        value={item.value}
                      >
                        {t(item.i18nKey, {defaultValue: item.id})}
                      </SelectItem>
                    ))}
                  </SelectField>
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="firstName"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.fiberInterest.firstName')}
                    id={`firstName-${id}`}
                    required={isRequired(fields.firstName)}
                    errorText={tSafe(errors.firstName?.message, t)}
                    maxLength={maxLength(fields.firstName)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="lastName"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.fiberInterest.lastName')}
                    id={`lastName-${id}`}
                    required={isRequired(fields.lastName)}
                    errorText={tSafe(errors.lastName?.message, t)}
                    maxLength={maxLength(fields.lastName)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="email"
                render={({field}) => (
                  <TextField
                    {...field}
                    type="email"
                    label={t('forms.fiberInterest.email')}
                    id={`email-${id}`}
                    required={isRequired(fields.email)}
                    errorText={tSafe(errors.email?.message, t)}
                    maxLength={maxLength(fields.email)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="phoneNumber"
                render={({field}) => (
                  <TextField
                    {...field}
                    type="tel"
                    label={t('forms.fiberInterest.phoneNumber')}
                    id={`phoneNumber-${id}`}
                    required={isRequired(fields.phoneNumber)}
                    errorText={tSafe(errors.phoneNumber?.message, t)}
                  />
                )}
              />
            </div>
            <div className="field">
              <div className={styles.terms}>
                <Controller
                  control={control}
                  name="termsAndConditions"
                  render={({field: {onChange, value}}) => (
                    <Checkbox
                      checked={value === YES}
                      onCheckedChange={checked => onChange(checked ? YES : '')}
                      id={`termsAndConditions-${id}`}
                    />
                  )}
                />
                <TermsAndConditions htmlFor={`termsAndConditions-${id}`} />
              </div>
              <FormError
                message={tSafe(errors.termsAndConditions?.message, t)}
              />
            </div>
            <SubmitButton
              isDisabled={isPending}
              isPending={isPending}
              rootError={getRootError(errors, isSubmitted, isValid, t)}
            />
          </fieldset>
          {constants.RECAPTCHA_SITE_KEY && (
            <RecaptchaScript siteKey={constants.RECAPTCHA_SITE_KEY} />
          )}
        </form>
      )}
    </>
  );
}

export {FiberInterestForm};
